import { formatDate, parseISO } from "date-fns"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Badge } from "design-system/atoms/Badge"
import { Separator } from "design-system/atoms/Separator"
import { Skeleton } from "design-system/atoms/Skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { GitCompareArrows, MessageSquareText, RefreshCw } from "lucide-react"
import { GorgiasIcon } from "modules/shared/icons/gorgias"
import { useGorgiasTicketActivityQuery } from "modules/support/hooks/useGorgiasTicketActivityQuery"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ConversationTicketIntegrationKindEnum,
  ConversationTicketIntegrationResponseDataSchema,
  SupportGorgiasTicketActivityResponseDataSchema,
} from "schema"
import { z } from "zod"

type Integration = z.infer<typeof ConversationTicketIntegrationResponseDataSchema>
type Activity = z.infer<typeof SupportGorgiasTicketActivityResponseDataSchema>
type Props = {
  integrations: Integration[]
} & ({ ticketId: string } | { issueId: string })

function getQueryProps(props: Props, enabled: boolean) {
  if ("ticketId" in props) return { ticketId: props.ticketId, enabled }

  return { issueId: props.issueId, enabled }
}

export function GorgiasActivityPanel(props: Props) {
  const { t } = useTranslation()
  const [accordionValue, setAccordionValue] = useState<string>()
  const integration = props.integrations.find(
    (item) => item.kind === ConversationTicketIntegrationKindEnum.Gorgias,
  )
  const { data, dataUpdatedAt, isError, isFetching, isPending } = useGorgiasTicketActivityQuery(
    getQueryProps(props, Boolean(integration)),
  )

  if (!integration) return null

  const fetchedAt = dataUpdatedAt ? formatDate(dataUpdatedAt, "p") : null
  const events = data?.events ?? []
  const messages = data?.messages ?? []

  return (
    <Accordion
      type="single"
      collapsible
      value={accordionValue}
      onValueChange={setAccordionValue}
      className="rounded-md border border-support-primary bg-muted/20"
    >
      <AccordionItem value="gorgias-activity" className="border-b-0">
        <AccordionTrigger className="px-4 py-2 bg-support-primary/5 data-[state=open]:rounded-b-none">
          <div className="flex w-full min-w-0 items-center justify-between gap-3 pr-2">
            <div className="flex min-w-0 items-center gap-2">
              <GorgiasIcon className="size-4 shrink-0" />
              <span className="truncate text-sm">
                {t("support.timeline.gorgiasActivity.title")}
              </span>
              <span className="shrink-0 text-sm">{`[#${integration.externalId}]`}</span>
            </div>
            <div className="flex shrink-0 items-center gap-4">
              {data?.ticket?.status && (
                <Badge
                  variant="outline"
                  className="shrink-0 capitalize bg-support-primary/5 border-support-primary text-support-primary"
                >
                  {data.ticket.status}
                </Badge>
              )}
              <div className="flex items-center gap-2">
                {isFetching && (
                  <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />
                )}
                {fetchedAt && (
                  <span className="hidden text-xs text-muted-foreground sm:inline">
                    {t("support.timeline.gorgiasActivity.fetchedAt", { time: fetchedAt })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4 pt-2">
          {isPending && <LoadingSlate />}
          {isError && <EmptySlate message={t("support.timeline.gorgiasActivity.fetchError")} />}
          {data?.ticket === null && (
            <EmptySlate message={t("support.timeline.gorgiasActivity.notFound")} />
          )}
          {data?.ticket && (
            <div className="flex flex-col gap-4">
              <Tabs defaultValue="messages">
                <TabsList>
                  <TabsTrigger value="messages">
                    <MessageSquareText className="size-4" />
                    <span>{t("support.timeline.gorgiasActivity.messages")}</span>
                    <Badge variant="secondary">{messages.length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="changes">
                    <GitCompareArrows className="size-4" />
                    <span>{t("support.timeline.gorgiasActivity.changes")}</span>
                    <Badge variant="secondary">{events.length}</Badge>
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="messages" className="flex flex-col gap-2">
                  {messages.length === 0 ? (
                    <EmptySlate message={t("support.timeline.gorgiasActivity.emptyMessages")} />
                  ) : (
                    messages.map((message) => <MessageRow key={message.id} message={message} />)
                  )}
                </TabsContent>
                <TabsContent value="changes" className="flex flex-col gap-2">
                  {events.length === 0 ? (
                    <EmptySlate message={t("support.timeline.gorgiasActivity.emptyChanges")} />
                  ) : (
                    events.map((event) => <EventRow key={event.id} event={event} />)
                  )}
                </TabsContent>
              </Tabs>
              <Separator />
              <TicketDetails activity={data} />
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

function MessageRow({ message }: { message: Activity["messages"][number] }) {
  const { t } = useTranslation()
  let messageKind = t("support.timeline.gorgiasActivity.message")

  if (message.public === false) {
    messageKind = t("support.timeline.gorgiasActivity.internalNote")
  }
  if (message.public === true) {
    messageKind = t("support.timeline.gorgiasActivity.publicReply")
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{messageKind}</span>
          {message.sender && (
            <span className="text-xs text-muted-foreground">{message.sender}</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {formatDate(parseISO(message.createdAt), "Pp")}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm text-muted-foreground">
        {message.body || t("support.timeline.gorgiasActivity.emptyMessage")}
      </p>
      {message.attachmentsCount > 0 && (
        <span className="text-xs text-muted-foreground">
          {t("support.timeline.gorgiasActivity.attachments", {
            count: message.attachmentsCount,
          })}
        </span>
      )}
    </div>
  )
}

function EventRow({ event }: { event: Activity["events"][number] }) {
  const { t } = useTranslation()
  const title = t(`support.timeline.gorgiasActivity.eventTypes.${event.type}`, {
    defaultValue: event.type,
  })

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="size-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {formatDate(parseISO(event.createdAt), "Pp")}
        </span>
      </div>
      <EventData value={event.data} />
    </div>
  )
}

function TicketDetails({ activity }: { activity: Activity }) {
  const { t } = useTranslation()
  const ticket = activity.ticket

  if (!ticket) return null

  return (
    <div className="flex flex-col gap-2">
      {ticket.subject && <div className="text-sm font-medium">{ticket.subject}</div>}
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
        {ticket.channel && (
          <span>{t("support.timeline.gorgiasActivity.channel", { value: ticket.channel })}</span>
        )}
        {ticket.assignee && (
          <span>{t("support.timeline.gorgiasActivity.assignee", { value: ticket.assignee })}</span>
        )}
        {ticket.createdAt && (
          <span>
            {t("support.timeline.gorgiasActivity.createdAt", {
              date: formatDate(parseISO(ticket.createdAt), "Pp"),
            })}
          </span>
        )}
        {ticket.updatedAt && (
          <span>
            {t("support.timeline.gorgiasActivity.updatedAt", {
              date: formatDate(parseISO(ticket.updatedAt), "Pp"),
            })}
          </span>
        )}
      </div>
      {ticket.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">
            {t("support.timeline.gorgiasActivity.tags")}
            {":"}
          </span>
          {ticket.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

function EventData({ value }: { value: unknown }) {
  const { t } = useTranslation()

  if (value === null || value === undefined) {
    return (
      <span className="text-xs text-muted-foreground">
        {t("support.timeline.gorgiasActivity.noEventData")}
      </span>
    )
  }

  return (
    <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-muted px-2 py-1.5 text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

function EmptySlate({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
      {message}
    </div>
  )
}

function LoadingSlate() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  )
}
